import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { getCategoryIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { isFalsy, isValidString, shouldDisplayAddPointElement } from '../helpers/utils';

import { PointCardBase } from './point-card-base';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('mini-point-card')
export class MiniPointCard extends PointCardBase {
    static amplitude_callsite: string = 'Mini Point Card';

    @state()
    multiSelectionMode: boolean = false;

    private async onCardClick() {
        this.dispatchEvent(new CustomEvent('on-card-click'));
    }

    private async onDeselectPoint() {
        if (this.point) {
            await topicData.mapComponent?.onDeselectPoint(this.point);
        }
    }

    render() {
        return html`
            <div class="mini-point-card" @mouseenter=${this.onHoverMarker} @mouseleave=${this.onExitHoverMarker}>
                ${this.multiSelectionMode ? html`
                        <div class="deselect-control" @click="${this.onDeselectPoint}">
                            <mwc-icon>close</mwc-icon>
                            <span>Deselect</span>
                        </div>`
                    : this.hasImages ?
                            html`<div class="mini-point-card__image-wrapper" @click="${this.onCardClick}">
                                <img @error="${(e: Event) => this.handleImageError(e)}" src=${this.firstImage} class="mini-point-card__image" style="object-fit: ${topicData.topic?.image_display ?? 'cover'}"/>
                                </div>`
                            : html`<div class="space-wrapper"></div>`
                    }
                <div class="${this.hasImages ? 'mini-point-card__content_image' :'mini-point-card__content_no_image'}" @click="${this.onCardClick}">
                    <span class="mini-point-card__category-wrapper">
                        ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined
                            ? html`<img @error="${(e: Event) => this.handleImageError(e)}" src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid),this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                        class="mini-point-card__point-number"
                                        width="28"
                                        height="28"/>`
                            : ''
                        }
                        <span class="mini-point-card__category-pill">
                            <img src="${getCategoryIcon(this.category?._id)}" width="16" height="16"/>
                            ${this.category?.name}
                        </span>
                    </span>
                    ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card) ? html`
                        <span class="mini-point-card__title">
                            ${this.point?.featured === true
                                ? html`
                                    <mwc-icon class="mini-point-card__title--featured">star</mwc-icon>`
                                : ''
                            }
                            ${isValidString(this.point?.name) ? html`<h2 class="mini-point-card__title-text">${this.point?.featured === true ? `${topicData.topic?.featured_name ?? 'Featured'}:` : ''} ${this.point?.name}</h2>`: ''}
                    </span>
                    ` : ''}
                    ${this.point?.location.place_id !== 'custom' && isFalsy(topicData.topic?.disable_location_ui) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.location?.hide_on_point_card)
                        ? html`<span class="mini-point-card__address">
                                ${this.point?.location.search === 'Shape' ? '' : this.point?.location.search}
                            </span>`
                        : ''
                    }
                    ${this.ratingStarsDisplay}
                    ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.description?.hide_on_point_card) ? html`
                        ${isValidString(this.point?.description) ? html`
                        <span class="mini-point-card__description">
                            ${unsafeHTML(this.descriptionString ?? '')}
                        </span>
                        <div class="mini-point-card__cover">
                        </div>
                        ` : ''}
                    ` : ''}
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 36px;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .mini-point-card {
                word-wrap: break-word;
                border-radius: 4px;
                display: flex;
                height: 150px;
                cursor: pointer;
                overflow-y: auto;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
            }

            .mini-point-card__content_image {
                padding: 12px 16px 12px 8px;
                width: 60%;
                background: var(--theme-white-color);
                overflow: hidden;
                opacity: 0.98;
                flex: 1;
            }

            .mini-point-card__content_no_image {
                padding: 12px 16px;
                width: 100%;
                background: white;
                overflow: hidden;
                opacity: 0.98;
            }

            .mini-point-card__image {
                width: 100%;
                height: 100%;
                border-radius: 4px;
                object-fit: cover;
                opacity: 1;
            }

            .mini-point-card__point-number {
                margin-right: 2px;
                z-index: 1;
            }

            .mini-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .mini-point-card__cover {
                height: 40px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }

            .mini-point-card__category-wrapper {
                display: flex;
                flex-wrap: wrap;
            }

            .mini-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 5px 8px;
                gap: 5px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .mini-point-card__image-wrapper {
                display: flex;
                width: 30%;
                background-color: var(--theme-white-color);
                overflow: hidden;
                padding: 8px;
                align-items: center;
                opacity: 0.98;
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .mini-point-card__title-text {
                font-weight: 700;
                font-size: 16px;
                color: var(--main-primary-color);
                margin: 4px 0 0;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 20px;
                width: 16px;
                padding-right: 4px;
            }

            .mini-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .mini-point-card__title {
                display: flex;
            }

            .deselect-control {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 1rem;
                gap: 0.25rem;
                cursor: pointer;
                background-color: var(--theme-opacity-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 2rem;
                font-size: 14px;
            }
        `;
    }
}